// #region Vue related packages
import { createApp } from 'vue';
import { initialiseAuth, Http } from '@lcp/http-auth';
import { createPinia } from 'pinia';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
// #endregion

// #region discretionary packages
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { createHead } from '@unhead/vue/client';
import 'flag-icons/css/flag-icons.min.css';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
// #endregion

// #region local global imports
import './fontAwesome.ts';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'leaflet/dist/leaflet.css';
import '@/assets/icofont/icofont.min.css';
import '@/assets/scss/main.scss';

// #endregion

// #region highcharts
import highcharts from 'highcharts/highcharts';
import More from 'highcharts/highcharts-more';
import Exporting from 'highcharts/modules/exporting';
import Offline from 'highcharts/modules/offline-exporting';
import stockInit from 'highcharts/modules/stock';
import Treemap from 'highcharts/modules/treemap';
import Histogram from 'highcharts/modules/histogram-bellcurve';
import Boost from 'highcharts/modules/boost';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import Timeline from 'highcharts/modules/timeline';
// #endregion

// #region local
import JsonExcel from 'vue-json-excel3';
import { router } from './router.ts';
import App from './App.vue';
import { primevueLcpTheme } from './assets/primevueLcpTheme.ts';
import { DEFAULT_TIMEZONE } from './constants/Time.ts';

// #endregion

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

More(highcharts);
Exporting(highcharts);
Offline(highcharts);
stockInit(highcharts);
Treemap(highcharts);
Boost(highcharts);
Histogram(highcharts);
NoDataToDisplay(highcharts);
Timeline(highcharts);

Http.initialise({ allowDuplicateApiCalls: true });

// #region auth initialisation
await initialiseAuth(
    {
        auth: {
            clientId: import.meta.env.VITE_APP_AUTH_CLIENT_ID,
            authority: import.meta.env.VITE_APP_AUTH_AUTHORITY,
            redirectUri: window.location.origin,
            postLogoutRedirectUri: window.location.origin,
            knownAuthorities: [import.meta.env.VITE_APP_AUTH_KNOWN_AUTHORITY],
        },
        cache: {
            cacheLocation: 'localStorage',
        },
    },
    [import.meta.env.VITE_APP_AUTH_SCOPE],
    'loginRedirect',
);
// #endregion

// #region pinia setup
const pinia = createPinia();
// #endregion

const head = createHead();

// #region app creation and component registration

createApp(App)
    .use(pinia)
    .use(router)
    .use(PrimeVue, {
        zIndex: {
            modal: 1100, // dialog, sidebar
            overlay: 1000, // dropdown, overlaypanel
            menu: 1000, // overlay menus
            tooltip: 9999, // tooltip
        },
        theme: {
            preset: primevueLcpTheme,
            options: {
                darkModeSelector: '.dark-mode',
                cssLayer: {
                    name: 'primeDefault',
                    order: 'base, utilities, primeDefault, primeCustom',
                },
            },
        },
        locale: {
            dateFormat: 'dd/mm/yy',
        },
    })
    .use(ConfirmationService)
    .use(ToastService)
    .use(head)
    .directive('tooltip', Tooltip)
    .component('font-awesome-icon', FontAwesomeIcon)
    .component('download-excel', JsonExcel)
    .mount('#app');
// #endregion
